import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
} from '@mui/material';
import { apiService } from '../services/api';
import LaunchIcon from '@mui/icons-material/Launch';

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    image: null,
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await apiService.getBanners();
      if (response.success) {
        setBanners(response.data || []);
      } else {
        setAlert({ severity: 'error', message: '❌ Failed to fetch banners' });
      }
    } catch (error) {
      setAlert({ severity: 'error', message: '❌ ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://bisauli.com/api3/controllers'}/BannerController.php?action=deleteBanner`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: bannerId }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setAlert({ severity: 'success', message: '✅ Banner deleted successfully' });
        fetchBanners();
        setOpenDialog(false);
      } else {
        setAlert({ severity: 'error', message: '❌ ' + data.message });
      }
    } catch (error) {
      setAlert({ severity: 'error', message: '❌ ' + error.message });
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#333', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          🎨 All Banners
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Total Banners: <Chip label={banners.length} color="primary" />
        </Typography>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.severity} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Form for Adding Banners */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Add New Banner
        </Typography>
        <Box component="form" noValidate autoComplete="off">
          <TextField
            label="Title"
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <Button
            variant="contained"
            component="label"
            sx={{ mb: 2 }}
          >
            Upload Image
            <input
              type="file"
              hidden
              onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
            />
          </Button>
          <Button variant="contained" color="primary">
            Submit
          </Button>
        </Box>
      </Box>

      {/* Banners Grid */}
      {banners.length === 0 ? (
        <Alert severity="info">
          📭 No banners found. Visit the admin panel to create one.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {banners.map((banner) => (
            <Grid item xs={12} sm={6} md={4} key={banner.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: 2,
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {/* Banner Image */}
                {banner.image_url && (
                  <CardMedia
                    component="img"
                    height="250"
                    image={banner.image_url}
                    alt={banner.title}
                    sx={{
                      objectFit: 'cover',
                      backgroundColor: '#f5f5f5',
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x250?text=Banner+Image';
                    }}
                  />
                )}

                {/* Banner Content */}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                    {banner.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {banner.subtitle}
                  </Typography>

                  {/* CTA Button Preview */}
                  {banner.cta && (
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        mb: 2,
                        borderColor: '#667eea',
                        color: '#667eea',
                        '&:hover': {
                          backgroundColor: '#667eea',
                          color: 'white',
                        },
                      }}
                    >
                      {banner.cta}
                    </Button>
                  )}

                  {/* Metadata */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      📅 {new Date(banner.created_at).toLocaleDateString()}
                    </Typography>
                    <Chip
                      label={banner.status || 'active'}
                      size="small"
                      color={banner.status === 'active' ? 'success' : 'default'}
                    />
                  </Box>
                </CardContent>

                {/* Actions */}
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    gap: 1,
                    borderTop: '1px solid #eee',
                    backgroundColor: '#f9f9f9',
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LaunchIcon />}
                    fullWidth
                    sx={{ color: '#667eea', borderColor: '#667eea' }}
                    href={banner.image_url}
                    target="_blank"
                  >
                    View
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this banner?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDeleteBanner(null)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Banners;